import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ADS_BATCH_MAX_COPIES, AdsBatchLayout, } from '@wit-support/admin-contracts';
import { Button, Checkbox, ConfirmDialog, Label, EmptyState, ErrorCard, InlineNotice, SectionCard, Skeleton, TextLink, toast, formatCount, } from '@wit-support/ui';
import { SubmitButton, ZodField, ZodForm, useZodFormContext } from '@wit-support/ui-forms';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Info, LayoutTemplate, TriangleAlert } from 'lucide-react';
import { adsIssuesOf, createBatch, listTemplates, previewBatch, setTemplateArchived, } from '../api.js';
import { RepeatBanner, isRepeatNotFound, loadRepeat, parseRepeatFrom, repeatFieldIssues, repeatUiItems, } from '../components/repeat.js';
import { AdsTime } from '../components/ads-time.js';
import { ADS_QUERY_DEFAULTS, REFERENCE_STALE_MS } from '../query-policy.js';
import { currencyScale } from '@wit-support/ui-domain';
import { newIdempotencyKey } from '../format.js';
import { useAdsLauncherPerms } from '../perms.js';
import { ADS_KEY, AdsScreen, TEMPLATES_KEY, describeAdsError, useAccounts, useAdsHealth, useCountryLists, useLocales, useRegistry, } from '../shared.js';
import { AccountChips, allReady, firstNotReadyText } from '../components/account-chips.js';
import { CREATE_FLOW_ACTION, ConfirmLaunchDialog, confirmSummaryKey, createFlowOf, isScheduledActive, } from '../components/confirm-launch-dialog.js';
import { IssuesList } from '../components/issues-list.js';
import { groupIssues, planIssues, visiblePlanIssues } from '../issues.js';
import { CreativeItems, toContractItems, validateItems, } from '../components/creative-items.js';
import { PreviewPanel } from '../components/preview-panel.js';
import { LaunchFieldGroupsNote, LaunchGroups, GROUPS_OFF_LAUNCH_TEXT, NO_GROUPS_FORM, launchGroupsFromInput, launchGroupsInput, launchGroupsSchema, } from '../components/launch-groups.js';
import { GROUP_BID_KEY, groupsFeature } from '../groups.js';
import { FunnelCatalogLoading, FunnelPlanSummary, LaunchFunnelStep, effectiveFunnelValues, funnelIssueLabel, hasTemplateFunnel, launchFunnelDefaults, launchFunnelInput, launchFunnelModel, launchFunnelSchema, useFunnelSettingsCatalog, } from '../funnel-settings.js';
import { MEDIA_SOURCE_LABEL_RU } from '../components/media-sources.js';
import { humanizeDisplay } from '../registry/value-display.js';
import { LaunchTemplateCards } from '../components/launch-template-cards.js';
import { RegistryDataProvider, StartWidget } from '../registry/field-widget.js';
import { FieldIssues, useIssuesUntilEdited, FixedFieldsList, RegistryFields, ValueChips, issuesByField, } from '../registry/registry-form.js';
import { NON_FORM_TYPES, buildRegistrySchema, fieldName, fromFormValues, fromUiValue, isVisible, toFormValues, toUiValue, validateValue, } from '../registry/to-zod.js';
import { allDescs, fixedDescs, launchEditableDescs, slotNamesOf, templateReadOnlyDescs, templateValuesWithDefaults, } from '../registry/use-registry.js';
import { LAYOUT_LABELS } from '../naming.js';
import { DUPLICATE_ACTIVATION_TEXT } from '../duplicate-texts.js';
import { LADDER_INPUT_FIELD, LADDER_KEYS, ladderKeyActive, LadderFields, LaunchSpendProvider, StructureOutcome, ladderState, spendFooterText, useLaunchSpend, } from '../components/launch-structure.js';
import { GENDERS_KEY, ADVANTAGE_KEY, GenderAdvantageNotice, } from '../components/gender-advantage-notice.js';
import { SpendLaunchConfirmDialog } from '../components/confirm-launch-dialog.js';
/** «Новый запуск» — один экран: шаблон → кабинеты → креативы → бюджет → старт → проверка. */
export function NewLaunchPage() {
    return (_jsx(AdsScreen, { title: "\u041D\u043E\u0432\u044B\u0439 \u0437\u0430\u043F\u0443\u0441\u043A", description: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0448\u0430\u0431\u043B\u043E\u043D, \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0438 \u043A\u0440\u0435\u0430\u0442\u0438\u0432\u044B \u2014 \u0432\u0441\u0451 \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u043E\u0435 \u0443\u0436\u0435 \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435 \u0438 \u043F\u0440\u043E\u0444\u0438\u043B\u0435 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430. \u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u00AB\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C\u00BB, \u043F\u043E\u0442\u043E\u043C \u0441\u043E\u0437\u0434\u0430\u0442\u044C.", children: _jsx(NewLaunchBody, {}) }));
}
function NewLaunchBody() {
    const registry = useRegistry();
    const accounts = useAccounts();
    const templates = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: TEMPLATES_KEY,
        queryFn: () => listTemplates(),
        staleTime: REFERENCE_STALE_MS,
    });
    const catalog = useFunnelSettingsCatalog();
    const perms = useAdsLauncherPerms();
    const qc = useQueryClient();
    const navigate = useNavigate();
    // «Создать такую же» (v4 §11.6): `?from=batch:<id>` / `?from=launch:<id>` —
    // перезагрузка страницы не теряет предзаполнение; только при новом auto-ads
    const health = useAdsHealth();
    const v4 = health.data?.features?.builderV4 === true;
    const search = useSearch({ strict: false });
    const from = v4 ? parseRepeatFrom(search.from) : null;
    // `?from=` пришёл раньше `/health` — ждём его, а не показываем пустую форму шаблона
    const waitHealth = search.from != null && search.from !== '' && health.isPending;
    const repeatQ = useQuery({
        ...ADS_QUERY_DEFAULTS,
        // не под `['ads']`: общая инвалидация после изменений не должна повторно звать
        // repeat-input (он ходит в Meta за постами, §7.1)
        queryKey: ['ads-repeat-input', from],
        queryFn: () => loadRepeat(from),
        enabled: from != null && from !== 'invalid',
        // Каждый заход в повтор — свежий ответ (Codex): шаблон могли поправить между
        // заходами (заметки, деньги, группы). Кэш не живёт после ухода с экрана
        // (`gcTime: 0`) и запрос идёт при каждом монтировании; пока форма открыта —
        // не перезапрашиваем (правки человека не затираются).
        staleTime: Infinity,
        gcTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
    });
    const repeatData = from != null && from !== 'invalid' ? (repeatQ.data ?? null) : null;
    // кабинеты и креативы пачки, перенесённые в другой шаблон (смена шаблона после предзаполнения)
    // `templateId: null` — шаблон ещё не выбран: перенос применится к первому выбранному
    const [carried, setCarried] = useState(null);
    const [pendingCarry, setPendingCarry] = useState(null);
    // текущие значения открытой формы повтора — для переноса при смене шаблона
    const formValuesRef = useRef(null);
    const [switchTo, setSwitchTo] = useState(null);
    const clearFrom = () => void navigate({ to: '/fb-launches/new', search: {}, replace: true });
    const unarchive = useMutation({
        mutationFn: (id) => setTemplateArchived(id, false),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
            void repeatQ.refetch();
        },
        onError: (e) => toast.error(describeAdsError(e, 'write')),
    });
    // Каталог пришёл после открытия формы — форма пересобирается (`gen`); значения,
    // которые человек уже ввёл, переносятся (`restore`).
    const [rebuild, setRebuild] = useState({
        gen: 0,
        restore: null,
    });
    const { refetch } = catalog;
    const control = useMemo(() => ({
        loading: catalog.isPending,
        latest: catalog.data ?? null,
        retrying: catalog.isFetching,
        retry: () => void refetch(),
        rebuild: (restore) => setRebuild((r) => ({ gen: r.gen + 1, restore })),
    }), [catalog.isPending, catalog.data, catalog.isFetching, refetch]);
    const [templateId, setTemplateId] = useState(null);
    const repeatTemplate = repeatData?.repeat.template ?? null;
    // повтор загружен — шаблон пачки выбран сам
    useEffect(() => {
        if (repeatTemplate && !repeatTemplate.archived)
            setTemplateId(repeatTemplate.id);
    }, [repeatTemplate]);
    // список шаблонов закэширован (5 мин): если повтор видит версию шаблона новее —
    // перечитать список, иначе форма построится по старой версии
    // Шаблона повтора нет среди активных в кэше (создан/разархивирован другим
    // человеком) или версия старее — перечитать список один раз на версию шаблона
    // (Codex); не нашёлся и после — честное сообщение, а не пустая форма.
    const listed = templates.data?.items.find((t) => t.id === repeatTemplate?.id && !t.archived);
    const listStale = repeatTemplate != null &&
        !repeatTemplate.archived &&
        templates.data != null &&
        (listed == null || listed.version < repeatTemplate.version);
    const refreshedFor = useRef(null);
    const repeatTemplateKey = repeatTemplate
        ? `${repeatTemplate.id}@${repeatTemplate.version}`
        : null;
    useEffect(() => {
        if (!listStale || repeatTemplateKey == null || refreshedFor.current === repeatTemplateKey)
            return;
        refreshedFor.current = repeatTemplateKey;
        void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    }, [listStale, repeatTemplateKey, qc]);
    // список новее повтора (шаблон сохранили после того, как повтор сверился с ним) —
    // перезапросить repeat-input один раз на версию списка (ревью SB-5 P2-1)
    const repeatOutdated = repeatTemplate != null && listed != null && listed.version > repeatTemplate.version;
    const repeatRefetchedFor = useRef(null);
    useEffect(() => {
        if (!repeatOutdated || listed == null || repeatRefetchedFor.current === listed.version)
            return;
        repeatRefetchedFor.current = listed.version;
        void repeatQ.refetch();
    }, [repeatOutdated, listed, repeatQ]);
    // «Сохранить черновик» — выбор человека для ЭТОГО повтора (живёт выше формы)
    const [draftChoice, setDraftChoice] = useState(null);
    const fromKey = typeof search.from === 'string' ? search.from : '';
    const listRefreshing = listStale && (templates.isFetching || refreshedFor.current !== repeatTemplateKey);
    // Валюта выбранного кабинета — чипы шаблонов показывают деньги в ней.
    const [currency, setCurrency] = useState(null);
    const err = registry.error ?? accounts.error ?? templates.error;
    if (err)
        return _jsx(ErrorCard, { children: describeAdsError(err) });
    if (!registry.data || !accounts.data || !templates.data || waitHealth) {
        return _jsx(Skeleton, { className: "h-64 w-full" });
    }
    /**
     * Кабинеты и креативы пачки — в форму другого шаблона; `?from=` убирается.
     * Шаблон не выбран (удалён/в архиве — «Выбрать другой шаблон») — перенос
     * ждёт первого выбранного шаблона (ревью SB-5 P1-3).
     */
    const carryTo = (t) => {
        const input = repeatData?.repeat.input ?? null;
        // ТЕКУЩИЕ кабинеты и креативы формы (правки человека — «Убрать» удалённый пост
        // и т. п.); формы нет (шаблон удалён/в архиве) — исходный вход пачки (Codex P2)
        const live = formValuesRef.current?.() ?? null;
        const carry = live
            ? { accounts: [...live.accounts], items: [...live.items] }
            : input
                ? {
                    accounts: [...input.platformAccountIds],
                    items: repeatUiItems(input.items, repeatData?.files ?? {}),
                }
                : null;
        if (carry && repeatData) {
            setCarried({ createdAt: repeatData.repeat.source.createdAt, templateId: t?.id ?? null });
            if (t)
                setRebuild((r) => ({ gen: r.gen + 1, restore: carry }));
            else
                setPendingCarry(carry);
        }
        else {
            setCarried(null);
        }
        setTemplateId(t?.id ?? null);
        clearFrom();
    };
    if (from === 'invalid' || (repeatQ.isError && isRepeatNotFound(repeatQ.error))) {
        return (_jsxs(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "repeat-not-found", children: [_jsx("span", { children: "\u041F\u0430\u0447\u043A\u0430 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430." }), ' ', _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: clearFrom, children: "\u041D\u0430\u0447\u0430\u0442\u044C \u0441 \u0448\u0430\u0431\u043B\u043E\u043D\u0430" })] }));
    }
    if (from != null && repeatQ.isError) {
        return (_jsxs(InlineNotice, { variant: "danger", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "repeat-failed", children: [_jsxs("span", { children: ["\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043F\u0430\u0447\u043A\u0443 \u2014 ", describeAdsError(repeatQ.error)] }), _jsxs("div", { className: "mt-2 flex flex-wrap gap-2", children: [_jsx(Button, { type: "button", size: "sm", onClick: () => void repeatQ.refetch(), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: clearFrom, children: "\u041D\u0430\u0447\u0430\u0442\u044C \u0441 \u0448\u0430\u0431\u043B\u043E\u043D\u0430" })] })] }));
    }
    if (from != null && !repeatData) {
        return _jsx(Skeleton, { className: "h-64 w-full", "data-testid": "repeat-loading" });
    }
    if (repeatData && repeatTemplate == null) {
        return (_jsxs(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "repeat-template-missing", children: [_jsx("span", { children: "\u0428\u0430\u0431\u043B\u043E\u043D \u044D\u0442\u043E\u0439 \u043F\u0430\u0447\u043A\u0438 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D." }), ' ', _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => carryTo(null), children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0434\u0440\u0443\u0433\u043E\u0439 \u0448\u0430\u0431\u043B\u043E\u043D" })] }));
    }
    if (repeatData && repeatTemplate?.archived) {
        return (_jsxs(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "repeat-template-archived", children: [_jsxs("span", { children: ["\u0428\u0430\u0431\u043B\u043E\u043D \u00AB", repeatTemplate.name, "\u00BB \u0432 \u0430\u0440\u0445\u0438\u0432\u0435."] }), _jsxs("div", { className: "mt-2 flex flex-wrap gap-2", children: [perms.canManageTemplates ? (_jsx(Button, { type: "button", size: "sm", disabled: unarchive.isPending, onClick: () => unarchive.mutate(repeatTemplate.id), children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C \u0448\u0430\u0431\u043B\u043E\u043D \u0438\u0437 \u0430\u0440\u0445\u0438\u0432\u0430 \u0438 \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C" })) : null, _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => carryTo(null), children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0434\u0440\u0443\u0433\u043E\u0439 \u0448\u0430\u0431\u043B\u043E\u043D" })] })] }));
    }
    const repeatRefreshing = repeatOutdated &&
        (repeatQ.isFetching || repeatRefetchedFor.current !== (listed?.version ?? null));
    if (repeatData && (listRefreshing || repeatRefreshing)) {
        return _jsx(Skeleton, { className: "h-64 w-full", "data-testid": "repeat-template-refreshing" });
    }
    if (repeatData && repeatTemplate && listStale) {
        // перечитали — шаблона пачки (этой версии) в списке всё равно нет
        return (_jsxs(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "repeat-template-unlisted", children: [_jsxs("span", { children: ["\u0428\u0430\u0431\u043B\u043E\u043D \u00AB", repeatTemplate.name, "\u00BB \u044D\u0442\u043E\u0439 \u043F\u0430\u0447\u043A\u0438 \u043D\u0435 \u043D\u0430\u0448\u0451\u043B\u0441\u044F \u0432 \u0441\u043F\u0438\u0441\u043A\u0435 \u0448\u0430\u0431\u043B\u043E\u043D\u043E\u0432 (\u0432\u0435\u0440\u0441\u0438\u044F", ' ', repeatTemplate.version, "). \u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u0435 \u2014 \u0438\u043B\u0438 \u0432\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u0440\u0443\u0433\u043E\u0439 \u0448\u0430\u0431\u043B\u043E\u043D: \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0438 \u043A\u0440\u0435\u0430\u0442\u0438\u0432\u044B \u043F\u0430\u0447\u043A\u0438 \u043F\u0435\u0440\u0435\u043D\u0435\u0441\u0443\u0442\u0441\u044F."] }), _jsxs("div", { className: "mt-2 flex flex-wrap gap-2", children: [_jsx(Button, { type: "button", size: "sm", onClick: () => void qc.invalidateQueries({ queryKey: TEMPLATES_KEY }), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => carryTo(null), children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0434\u0440\u0443\u0433\u043E\u0439 \u0448\u0430\u0431\u043B\u043E\u043D" })] })] }));
    }
    const active = templates.data.items.filter((t) => !t.archived);
    if (active.length === 0) {
        return (_jsx(EmptyState, { icon: LayoutTemplate, title: "\u0428\u0430\u0431\u043B\u043E\u043D\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u2014 \u0441\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u043F\u0435\u0440\u0432\u044B\u0439", description: "\u0417\u0430\u043F\u0443\u0441\u043A \u0441\u0442\u0440\u043E\u0438\u0442\u0441\u044F \u0438\u0437 \u0448\u0430\u0431\u043B\u043E\u043D\u0430: \u0432 \u043D\u0451\u043C \u0441\u0442\u0440\u0443\u043A\u0442\u0443\u0440\u0430, \u0442\u0430\u0440\u0433\u0435\u0442\u0438\u043D\u0433, \u043F\u0440\u0430\u0432\u0438\u043B\u0430 \u0438 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044F. \u0413\u043E\u0442\u043E\u0432\u044B\u0445 \u0448\u0430\u0431\u043B\u043E\u043D\u043E\u0432 \u043D\u0435\u0442 \u2014 \u0438\u0445 \u0441\u043E\u0437\u0434\u0430\u0451\u0442\u0435 \u0432\u044B.", action: _jsx(Button, { asChild: true, children: _jsx(Link, { to: "/fb-launches/templates/new", children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0448\u0430\u0431\u043B\u043E\u043D" }) }) }));
    }
    const template = active.find((t) => t.id === templateId) ?? null;
    // Шаг «Настройки воронки» строится по каталогу: ждём его, только если у
    // шаблона настройки есть (сбой каталога не мешает — шаг скажет об этом).
    const waitCatalog = template != null && hasTemplateFunnel(template.funnelSettings) && catalog.isPending;
    // повтор применяется к форме, пока выбран шаблон пачки
    // повтор применяется к форме, только если сверен с ЭТОЙ версией шаблона
    const repeat = repeatData &&
        template != null &&
        template.id === repeatTemplate?.id &&
        template.version === repeatTemplate.version
        ? repeatData
        : null;
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [repeatData ? _jsx(RepeatBanner, { repeat: repeatData.repeat }) : null, carried &&
                !repeatData &&
                (carried.templateId == null || carried.templateId === template?.id) ? (_jsx(InlineNotice, { variant: "info", bordered: true, as: "div", "data-testid": "repeat-carried", children: carried.templateId == null ? (_jsxs(_Fragment, { children: ["\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0448\u0430\u0431\u043B\u043E\u043D \u2014 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0438 \u043A\u0440\u0435\u0430\u0442\u0438\u0432\u044B \u0438\u0437 \u043F\u0430\u0447\u043A\u0438 \u043E\u0442", ' ', _jsx(AdsTime, { iso: carried.createdAt, preset: "dateTimeNumeric" }), " \u043F\u0435\u0440\u0435\u043D\u0435\u0441\u0443\u0442\u0441\u044F \u0432 \u043D\u0435\u0433\u043E."] })) : (_jsxs(_Fragment, { children: ["\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0438 \u043A\u0440\u0435\u0430\u0442\u0438\u0432\u044B \u2014 \u0438\u0437 \u043F\u0430\u0447\u043A\u0438 \u043E\u0442", ' ', _jsx(AdsTime, { iso: carried.createdAt, preset: "dateTimeNumeric" }), ", \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u043E\u0435 \u2014 \u0438\u0437 \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u0433\u043E \u0448\u0430\u0431\u043B\u043E\u043D\u0430."] })) })) : null, _jsx(SectionCard, { title: "1. \u0428\u0430\u0431\u043B\u043E\u043D", children: _jsx(LaunchTemplateCards, { templates: active, selectedId: template?.id ?? null, onSelect: (t) => {
                        if (repeatData && t.id !== template?.id) {
                            // смена шаблона после предзаполнения — с подтверждением (§11.6)
                            setSwitchTo(t);
                            return;
                        }
                        if (pendingCarry) {
                            // первый выбор после «Выбрать другой шаблон» — кабинеты и креативы пачки в него
                            setTemplateId(t.id);
                            setRebuild((r) => ({ gen: r.gen + 1, restore: pendingCarry }));
                            setCarried((c) => (c ? { ...c, templateId: t.id } : c));
                            setPendingCarry(null);
                            return;
                        }
                        setTemplateId(t.id);
                        setRebuild((r) => ({ gen: r.gen, restore: null }));
                        // перенос действовал для прежнего шаблона — плашку убрать
                        if (carried && carried.templateId !== t.id)
                            setCarried(null);
                    }, registry: registry.data, currencyOf: (t) => currency ?? launchCurrency(accounts.data.items, [], t) }) }), template && waitCatalog ? (_jsx(FunnelCatalogLoading, { height: "h-64" })) : template ? (_jsx(LaunchForm, { template: template, repeat: repeat, valuesRef: formValuesRef, draftChoice: draftChoice?.from === fromKey ? { value: draftChoice.value } : null, onDraftChange: (value) => setDraftChoice({ from: fromKey, value }), catalog: catalog.data ?? null, catalogError: catalog.error, control: control, restore: rebuild.restore, registry: registry.data, accounts: accounts.data.items, onTemplateConflict: () => setTemplateId(template.id), onCurrencyChange: setCurrency }, `${template.id}@${template.version}#${rebuild.gen}${repeat ? '#repeat' : ''}`)) : (_jsx(InlineNotice, { variant: "muted", as: "div", children: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0448\u0430\u0431\u043B\u043E\u043D \u2014 \u044D\u043A\u0440\u0430\u043D \u043F\u043E\u0434\u0441\u0442\u0430\u0432\u0438\u0442 \u0432\u0441\u0435 \u0435\u0433\u043E \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F." })), _jsx(ConfirmDialog, { open: switchTo != null, onOpenChange: (o) => (!o ? setSwitchTo(null) : null), title: "\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u0448\u0430\u0431\u043B\u043E\u043D?", description: "\u041F\u0435\u0440\u0435\u043D\u0435\u0441\u0443\u0442\u0441\u044F \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0438 \u043A\u0440\u0435\u0430\u0442\u0438\u0432\u044B, \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u043E\u0435 \u2014 \u0438\u0437 \u043D\u043E\u0432\u043E\u0433\u043E \u0448\u0430\u0431\u043B\u043E\u043D\u0430.", cancelLabel: "\u041E\u0441\u0442\u0430\u0432\u0438\u0442\u044C", actionLabel: "\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u0448\u0430\u0431\u043B\u043E\u043D", actionStyle: "primary", onConfirm: () => {
                    carryTo(switchTo);
                    setSwitchTo(null);
                } })] }));
}
/**
 * Поля шага 4, которые — «деньги» (бюджет, ставка, стратегия): только они —
 * прежний «4. Бюджет и ставка»; иначе — «4. Бюджет, ставка и изменения
 * шаблона» с подзаголовками групп (v4 §11.2).
 */
const MONEY_LAUNCH_KEYS = new Set([
    'budget.dailyMinor',
    'adset.bidAmountMinor',
    'adset.roasFloor',
    'campaign.bidStrategy',
    'campaign.budgetLevel',
]);
/** Предел групп объявлений в одной кампании, если реестр его не прислал (v4 §3.9). */
const MAX_ADSETS_PER_CAMPAIGN = 50;
/**
 * Валюта денег экрана запуска: первого выбранного кабинета (как пояс старта),
 * а пока кабинет не выбран — общая валюта кабинетов шаблона. null — неизвестна.
 */
export function launchCurrency(accounts, selected, template) {
    const byId = new Map(accounts.map((a) => [a.platformAccountId, a.currency ?? null]));
    if (selected.length > 0)
        return byId.get(selected[0]) ?? null;
    const own = new Set(template.platformAccountIds.map((id) => byId.get(id) ?? null));
    return own.size === 1 ? ([...own][0] ?? null) : null;
}
/**
 * Лесенка прошлой пачки → значения опций реестра (`opt.bidStep*`) поверх
 * шаблона; убранная граница (`null`) — пусто.
 */
function repeatLadderValues(tplValues, input) {
    const out = { ...tplValues };
    for (const key of LADDER_KEYS) {
        const v = input[LADDER_INPUT_FIELD[key]];
        if (v === undefined)
            continue;
        out[key] = v === null ? undefined : v;
    }
    return out;
}
/** Ввод пачки без `activation` — для сравнения «план про текущий экран?». */
function comparable(input) {
    if (!input)
        return '';
    const { activation: _a, ...rest } = input;
    return JSON.stringify(rest);
}
export function LaunchForm({ template, catalog, catalogError, registry, accounts, onTemplateConflict, onCurrencyChange, control, restore, repeat = null, valuesRef, draftChoice = null, onDraftChange, duplicate = false, initialIdempotencyKey, confirmNote, blockedReason, existingBatchIds, }) {
    const perms = useAdsLauncherPerms();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const health = useAdsHealth();
    const locales = useLocales();
    const countryLists = useCountryLists();
    // Ключ пачки живёт, пока создание не удалось ЗАНЯТЬ его: отказ auto-ads
    // (`invalid-values`, `preview-stale`) ключ не резервирует (проверка идёт до
    // записи пачки), поэтому повтор идёт с тем же ключом — двойной клик не
    // создаст две пачки. Новый ключ — только когда этот уже занят другой пачкой.
    const [idempotencyKey, setIdempotencyKey] = useState(() => initialIdempotencyKey ?? newIdempotencyKey());
    const [previewed, setPreviewed] = useState(null);
    // повтор: прошедший старт — замечание у поля «Старт» (пустое и подсвечено)
    const [serverIssues, setServerIssues] = useState(() => repeat ? repeatFieldIssues(repeat.repeat.notes) : []);
    const repeatInput = repeat?.repeat.input ?? null;
    // повтор черновика: переключатель «Сохранить черновик» стоит, как в прошлый раз (§11.6)
    // галочка «Сохранить черновик» живёт выше формы — пересборка формы её не сбрасывает
    const draft = draftChoice?.value ?? repeatInput?.mode === 'draft';
    const setDraft = (v) => onDraftChange?.(v);
    const [confirm, setConfirm] = useState(null);
    const [conflictOpen, setConflictOpen] = useState(false);
    // Копия кампании: ключ `dup-…` уже занят пачкой, созданной с другими значениями. Старую пачку
    // молча не открываем — человек выбирает: открыть её или создать ещё одну с текущими значениями.
    const [keyTaken, setKeyTaken] = useState(null);
    // Повтор создания нашёл пачку с этим ключом недостроенной и достроить её по
    // плану нельзя (409 `batch-resume-conflict`): ссылка на неё — до новой проверки.
    const [resumeConflict, setResumeConflict] = useState(null);
    const tplValues = useMemo(() => templateValuesWithDefaults(registry, template), [registry, template]);
    const launchDescs = useMemo(() => launchEditableDescs(registry, template), [registry, template]);
    // Снимок каталога при открытии формы (форма пересоздаётся с новой версией
    // шаблона): фоновое обновление каталога не меняет схему открытой формы.
    const [funnel] = useState(() => launchFunnelModel(template.funnelSettings, catalog, catalogError));
    const readOnly = useMemo(() => templateReadOnlyDescs(registry, template), [registry, template]);
    const fixed = useMemo(() => fixedDescs(registry), [registry]);
    const byKey = useMemo(() => new Map(allDescs(registry).map((d) => [d.key, d])), [registry]);
    const startDesc = byKey.get('schedule.start');
    const layoutDesc = byKey.get('opt.layout');
    // v4 (шаг «5. Структура», деньги до «Проверить») — только при новом auto-ads;
    // лесенка — ещё и если реестр отдаёт `opt.bidStep*`
    const v4 = health.data?.features?.builderV4 === true;
    const ladderDescs = useMemo(() => v4
        ? registry.options.filter((d) => d.enabled && LADDER_KEYS.includes(d.key))
        : [], [registry, v4]);
    // группы шаблона (v4 §11.2) — только при `features.adSetGroups`; сбой /health — запуск не проверяется
    const groupsOn = groupsFeature(health.data, health.isError, health.isPending);
    const tplGroups = useMemo(() => template.adSetGroups?.groups ?? [], [template]);
    const bidEditable = launchDescs.some((d) => d.key === GROUP_BID_KEY);
    // повтор: режим включения исходной пачки, а не шаблона (§7.3 п. 8; Codex P2 — ручная
    // пачка при шаблоне с автовключением не должна стать автоматической)
    const defaultActivation = repeat?.repeat.input?.activation ??
        (tplValues['opt.activation'] === 'manual' ? 'manual' : 'auto');
    // «Как в Ads Manager» (по умолчанию): одна кнопка — режим включения задан
    // шаблоном. Автовключение — «Создать» (включёнными с датой старта), ручное —
    // «Создать на паузе» (включает человек). Шаблон «на паузе» — прежние
    // «Создать на паузе» / «Создать и включить».
    const scheduledActive = isScheduledActive(tplValues['opt.createMode']);
    const currencyOf = (ids) => launchCurrency(accounts, ids, template);
    const defaultAccounts = repeatInput
        ? [...repeatInput.platformAccountIds]
        : template.platformAccountIds.length === 1
            ? [...template.platformAccountIds]
            : [];
    // Валюта меняется вместе с кабинетом; схема (memo) читает её через ref —
    // его обновляет `FormBody` по выбранным кабинетам.
    const currencyRef = useRef(currencyOf(defaultAccounts));
    // живые контрактные значения (шаблон + изменения + лесенка) — для проверки
    // лесенки по действующим стратегии и направлению; обновляет шаг 5
    const liveRef = useRef(tplValues);
    const schema = useMemo(() => z.object({
        accounts: z.array(z.string()).min(1, { message: 'Выберите хотя бы один кабинет' }),
        items: z.array(z.any()).superRefine((items, ctx) => {
            const msg = validateItems(items);
            if (msg)
                ctx.addIssue({ code: 'custom', message: msg });
        }),
        overrides: buildRegistrySchema(launchDescs, {
            context: () => tplValues,
            currency: () => currencyRef.current,
        }),
        copies: z
            .number({ message: 'Нужно число' })
            .int()
            .min(1, { message: 'От 1' })
            .max(ADS_BATCH_MAX_COPIES, { message: `Не больше ${ADS_BATCH_MAX_COPIES}` })
            .meta({
            label: 'Копий',
            description: 'Каждая копия — отдельная кампания (или группа — по раскладке).',
        }),
        layout: AdsBatchLayout.meta({
            label: 'Раскладка',
            options: AdsBatchLayout.options.map((v) => ({
                value: v,
                label: layoutDesc?.options?.find((o) => o.value === v)?.label ?? LAYOUT_LABELS[v],
            })),
        }),
        start: z.any().superRefine((v, ctx) => {
            const msg = startDesc
                ? validateValue(startDesc, v)
                : v?.mode === 'at' && !v.at
                    ? 'Укажите дату и время'
                    : null;
            if (msg)
                ctx.addIssue({ code: 'custom', message: msg });
        }),
        funnel: launchFunnelSchema(funnel),
        // неактивная граница лесенки (против направления шага) не проверяется (Codex #340)
        ladder: buildRegistrySchema(ladderDescs, {
            context: () => liveRef.current,
            currency: () => currencyRef.current,
            isActive: (d, all) => ladderKeyActive(d.key, all),
        }).optional(),
        groups: launchGroupsSchema({
            groups: tplGroups,
            feature: groupsOn,
            currency: () => currencyRef.current,
            sends: () => {
                const live = liveRef.current;
                const bidDesc = byKey.get(GROUP_BID_KEY);
                return {
                    bid: bidEditable && bidDesc != null && isVisible(bidDesc, live),
                    budget: live['campaign.budgetLevel'] === 'ABO',
                };
            },
        }),
    }), [launchDescs, tplValues, startDesc, layoutDesc, funnel, ladderDescs, tplGroups, groupsOn]);
    // поля, от которых зависят деньги и структура: без ошибок в них — можно спросить сервер
    const spendFieldsSchema = useMemo(() => schema.pick({ overrides: true, ladder: true, copies: true, layout: true, groups: true }), [schema]);
    // повтор: настройки воронки прошлой пачки (только разрешённые шаблоном — их отобрал сервер)
    const funnelDefaults = repeatInput?.funnelSettings
        ? launchFunnelDefaults({
            ...funnel,
            values: effectiveFunnelValues(funnel.values, repeatInput.funnelSettings),
        })
        : launchFunnelDefaults(funnel);
    const templateDefaults = {
        accounts: defaultAccounts,
        items: [],
        overrides: toFormValues(launchDescs, tplValues, { currency: currencyRef.current }),
        copies: typeof tplValues['opt.copies'] === 'number' ? tplValues['opt.copies'] : 1,
        layout: AdsBatchLayout.safeParse(tplValues['opt.layout']).success
            ? tplValues['opt.layout']
            : 'campaign_per_item',
        start: tplValues['schedule.start'] ?? { mode: 'now' },
        funnel: funnelDefaults,
        ladder: toFormValues(ladderDescs, tplValues, { currency: currencyRef.current }),
        groups: NO_GROUPS_FORM,
    };
    const computedDefaults = repeatInput
        ? {
            ...templateDefaults,
            items: repeatUiItems(repeatInput.items, repeat?.files ?? {}),
            overrides: toFormValues(launchDescs, { ...tplValues, ...(repeatInput.overrides ?? {}) }, { currency: currencyRef.current }),
            copies: repeatInput.copies ?? templateDefaults.copies,
            layout: repeatInput.layout ?? templateDefaults.layout,
            // старт прошёл (`start: null`) — «В дату» пустым, поле подсвечено замечанием
            start: repeatInput.start ?? { mode: 'at', at: '' },
            ladder: toFormValues(ladderDescs, repeatLadderValues(tplValues, repeatInput), {
                currency: currencyRef.current,
            }),
            // группы прошлой пачки: снятые — без галочки, новые группы шаблона — без галочки,
            // «в шаблоне появились группы» — все отмечены ([Р] 100)
            groups: launchGroupsFromInput(repeatInput.adSetGroups, tplGroups, {
                newKeys: repeat?.repeat.newGroupKeys ?? [],
                appeared: (repeat?.repeat.notes ?? []).some((n) => n.code === 'groups-appeared'),
                currency: currencyRef.current,
            }),
        }
        : templateDefaults;
    const defaultValues = restore
        ? {
            ...computedDefaults,
            ...restore,
            funnel: funnelDefaults,
        }
        : computedDefaults;
    // У шаблона настройки воронки, а каталога в форме нет — auto-ads план не примет
    const funnelBlocked = funnel.catalog == null && hasTemplateFunnel(template.funnelSettings);
    const labelOf = (k) => funnelIssueLabel(k, funnel.catalog) ?? byKey.get(k)?.label ?? k;
    /** Значения формы → тело `preview`/`create`. Без проверки (её сделала схема). */
    const buildInput = (v, activation) => {
        // Деньги формы — в валюте кабинета пачки: «25» в JPY — ¥25, в USD — 2500 центов.
        const currency = currencyOf(v.accounts ?? []);
        // только отличия от шаблона по разрешённым ключам; нет — поле не уходит
        const funnelSettings = launchFunnelInput(funnel, v.funnel);
        return {
            idempotencyKey,
            templateId: template.id,
            templateVersion: template.version,
            platformAccountIds: v.accounts,
            items: toContractItems(v.items ?? []),
            // Тот же контекст, что у показа полей (`RegistryFields contextValues`):
            // спрятанное `visibleWhen` поле не уходит в overrides.
            overrides: fromFormValues(launchDescs, v.overrides, { context: tplValues, currency }),
            copies: v.copies,
            layout: v.layout,
            start: v.start,
            activation,
            source: 'admin',
            ...(funnelSettings ? { funnelSettings } : {}),
            ...ladderInput(v.ladder, currency, v.overrides),
            // повтор: режим прошлой пачки (черновик) — как был
            ...(repeatInput && draft ? { mode: 'draft' } : {}),
            // группы: шаблон с группами при включённом флаге — из формы (повтор её предзаполнил);
            // иначе выбор прошлой пачки уходит как был — сервер ответит явным отказом, а не
            // молчаливым «все группы» (интеграция SB-5 + SB-7)
            ...(groupsOn === 'on' && tplGroups.length > 0
                ? groupsInput(v, currency)
                : repeatInput?.adSetGroups
                    ? { adSetGroups: repeatInput.adSetGroups }
                    : {}),
        };
    };
    /** Группы шаблона → `adSetGroups` входа (v4 §3.5); без групп / флаг выкл — поля нет. */
    const groupsInput = (v, currency) => {
        if (groupsOn !== 'on' || tplGroups.length === 0)
            return {};
        const overrides = fromFormValues(launchDescs, v.overrides, { context: tplValues, currency });
        const merged = { ...tplValues, ...overrides };
        const abo = merged['campaign.budgetLevel'] === 'ABO';
        const bidDesc = byKey.get(GROUP_BID_KEY);
        const bidVisible = bidDesc != null && isVisible(bidDesc, merged);
        const adSetGroups = launchGroupsInput(v.groups, tplGroups, {
            currency,
            bidEditable,
            abo,
            bidVisible,
        });
        return adSetGroups ? { adSetGroups } : {};
    };
    /**
     * Лесенка запуска → поля входа `bidStep*` (v4 §3.3): только отличия от
     * шаблона (вход старого вида не меняется); очищенная граница шаблона — `null`
     * (убрать на этот запуск).
     */
    const ladderInput = (ladder, currency, overrides) => {
        if (ladderDescs.length === 0)
            return {};
        const now = fromFormValues(ladderDescs, ladder, { currency });
        const live = {
            ...tplValues,
            ...fromFormValues(launchDescs, overrides, { currency }),
            ...now,
        };
        const out = {};
        for (const d of ladderDescs) {
            const field = LADDER_INPUT_FIELD[d.key];
            const v = now[d.key];
            const bound = field === 'bidFloorMinor' || field === 'bidCeilingMinor';
            // граница против направления шага не действует — не отправляется (Codex #340);
            // шаг и направление — всегда (кнопка «Убрать шаг ставки» при стратегии без ставки)
            if (bound && !ladderKeyActive(d.key, live))
                continue;
            if (v !== undefined && validateValue(d, v, currency) !== null)
                continue;
            if (JSON.stringify(v ?? null) === JSON.stringify(tplValues[d.key] ?? null))
                continue;
            if (v === undefined) {
                // очищенная граница — убрать границу шаблона; очищенный шаг — «выкл» (0, §3.3):
                // `bidStepMinor` не nullable, а без поля сервер взял бы шаг шаблона (ревью SB-4 P1-1)
                if (field === 'bidFloorMinor' || field === 'bidCeilingMinor')
                    out[field] = null;
                else if (field === 'bidStepMinor')
                    out[field] = 0;
                continue;
            }
            out[field] = v;
        }
        return out;
    };
    const onRejected = (err) => {
        if (err instanceof ModuleRequestError && err.upstreamCode === 'template-version-conflict') {
            setConflictOpen(true);
            return;
        }
        const issues = adsIssuesOf(err);
        if (issues.length > 0)
            setServerIssues(issues);
        toast.error(describeAdsError(err, 'write'));
    };
    const preview = useMutation({
        mutationFn: (input) => previewBatch(input).then((p) => ({ input, preview: p })),
        onSuccess: (res) => {
            setPreviewed(res);
            setServerIssues([]);
            setResumeConflict(null);
            setKeyTaken(null);
        },
        onError: onRejected,
    });
    /**
     * «Создать …»: режим включения — часть входа preview (от него зависят план
     * и хеш). Если план считался под другой режим, он сначала пересчитывается
     * под выбранную кнопку, и подтверждение показывает НОВЫЕ цифры — с
     * пометкой, если итоги или окно старта отличаются от уже показанных.
     */
    const requestCreate = async (mode) => {
        if (!previewed)
            return;
        if (previewed.input.activation === mode) {
            setConfirm({ mode, changed: false });
            return;
        }
        const seen = confirmSummaryKey(previewed.preview);
        const res = await preview
            .mutateAsync({ ...previewed.input, activation: mode })
            .catch(() => null);
        if (!res)
            return;
        if (!res.preview.canCreate) {
            toast.error('В плане есть ошибки — создание невозможно');
            return;
        }
        setConfirm({ mode, changed: confirmSummaryKey(res.preview) !== seen });
    };
    // Копия: пачка с этим ключом уже была (повтор / достройка) — admin-api отвечает на неё так же
    // (201), без отдельного признака; узнаём её по списку пачек копии, загруженному при открытии.
    const onCreated = (b) => {
        const existing = duplicate && (existingBatchIds ?? []).includes(b.id);
        toast.success(existing
            ? 'Открыта уже созданная из этой копии пачка'
            : draft
                ? 'Черновик пачки сохранён'
                : 'Пачка создана');
        void qc.invalidateQueries({ queryKey: ADS_KEY });
        void navigate({ to: '/fb-launches/batches/$id', params: { id: b.id } });
    };
    const create = useMutation({
        mutationFn: async (activation) => {
            // Подтверждение открывается только по плану под этот режим (requestCreate).
            if (!previewed || previewed.input.activation !== activation) {
                throw new ModuleRequestError('upstream_rejected', 409, {
                    serverMessage: 'Настройки изменились — проверьте ещё раз',
                    upstreamCode: 'preview-stale',
                });
            }
            return createBatch({ ...previewed.input, previewHash: previewed.preview.previewHash });
        },
        onSuccess: (b) => onCreated(b),
        onError: (err) => {
            if (err instanceof ModuleRequestError && err.upstreamCode === 'preview-stale') {
                toast.warning('Настройки изменились — проверьте ещё раз. План пересчитан.');
                if (previewed)
                    preview.mutate(previewed.input);
                return;
            }
            if (err instanceof ModuleRequestError && err.upstreamCode === 'idempotency-key-taken') {
                const id = err.details.batchId;
                if (duplicate) {
                    setKeyTaken({ batchId: typeof id === 'string' ? id : null });
                    return;
                }
                if (typeof id === 'string') {
                    toast.info('Эта пачка уже создана — открываем её');
                    void navigate({ to: '/fb-launches/batches/$id', params: { id } });
                    return;
                }
                // Ключ занят, а какой пачкой — неизвестно: с ним создать уже нельзя.
                // Новый ключ меняет вход — план устаревает, нужна новая проверка.
                setIdempotencyKey(newIdempotencyKey());
                toast.error('Ключ этой пачки уже занят другой пачкой. Нажмите «Проверить» ещё раз — будет создана новая пачка.');
                return;
            }
            if (err instanceof ModuleRequestError && err.upstreamCode === 'batch-resume-conflict') {
                // Пачка с этим ключом создана не вся, а достроить её по плану нельзя
                // (сменились профиль, медиа, список стран или реестр). С этим ключом
                // создать уже нечего — новый ключ; он меняет вход, план устаревает,
                // нужна новая проверка. Старую пачку оператор отменяет сам.
                setIdempotencyKey(newIdempotencyKey());
                const id = err.details.batchId;
                const missing = err.details.missing;
                setResumeConflict({
                    batchId: typeof id === 'string' ? id : null,
                    missing: Array.isArray(missing) ? missing.length : 0,
                });
                toast.error('Прошлую пачку не удалось достроить — подробности над кнопками.');
                return;
            }
            onRejected(err);
        },
    });
    // «Создать ещё одну с текущими значениями»: те же значения под новым ключом. Новый ключ меняет
    // вход, поэтому план пересчитывается, а создание идёт по свежему хешу этого плана.
    const createAnother = useMutation({
        mutationFn: async () => {
            if (!previewed) {
                throw new ModuleRequestError('upstream_rejected', 409, {
                    serverMessage: 'Настройки изменились — проверьте ещё раз',
                    upstreamCode: 'preview-stale',
                });
            }
            const key = newIdempotencyKey();
            const input = { ...previewed.input, idempotencyKey: key };
            const fresh = await previewBatch(input);
            if (!fresh.canCreate) {
                throw new ModuleRequestError('upstream_rejected', 409, {
                    serverMessage: 'В плане есть ошибки — создание невозможно',
                    upstreamCode: 'preview-stale',
                });
            }
            const b = await createBatch({ ...input, previewHash: fresh.previewHash });
            setIdempotencyKey(key);
            return b;
        },
        onSuccess: (b) => {
            setKeyTaken(null);
            onCreated(b);
        },
        onError: onRejected,
    });
    const firstAccountOf = (ids) => accounts.find((a) => a.platformAccountId === ids[0]) ?? null;
    return (_jsxs(ZodForm, { schema: schema, defaultValues: defaultValues, className: "flex flex-col gap-6", onSubmit: (values) => {
            if (blockedReason !== undefined)
                return undefined;
            // План считается под режим включения шаблона: при ручном включении и
            // «как в Ads Manager» — тоже на паузе (auto-ads: `manual` всегда на паузе).
            const input = buildInput(values, defaultActivation);
            return preview.mutateAsync(input).then(() => undefined, () => undefined);
        }, children: [valuesRef ? _jsx(FormValuesBridge, { target: valuesRef }) : null, _jsxs(SpendBridge, { enabled: v4, isValid: (v) => spendFieldsSchema.safeParse(v).success, buildInput: (v) => buildInput(v, previewed?.input.activation ?? defaultActivation), children: [_jsx(FormBody, { template: template, funnel: funnel, registry: registry, accounts: accounts, byKey: byKey, launchDescs: launchDescs, readOnly: readOnly, fixed: fixed, tplValues: tplValues, startDesc: startDesc, health: health.data, locales: locales.data ?? null, countryLists: countryLists.data?.items ?? null, canUpload: perms.canUpload, 
                        // итог в день — крупно в «Получится», футере и плане, не строкой под полем бюджета
                        issues: [...serverIssues, ...visiblePlanIssues(previewed?.preview.issues ?? [])], launchIssues: previewed ? planIssues(previewed.preview) : [], firstAccountOf: firstAccountOf, currencyOf: currencyOf, currencyRef: currencyRef, onCurrencyChange: onCurrencyChange, funnelControl: control, v4: v4, liveRef: liveRef, ladderDescs: ladderDescs, previewed: previewed, buildInput: (v) => buildInput(v, previewed?.input.activation ?? defaultActivation), tplGroups: tplGroups, groupsFeature: groupsOn, bidEditable: bidEditable, onRetryHealth: () => void health.refetch() }), previewed ? (_jsx(StalePreview, { previewed: previewed, buildInput: (v) => buildInput(v, previewed.input.activation), registry: registry, byKey: byKey, accounts: accounts, tplValues: tplValues, funnel: funnel, showFunnel: hasTemplateFunnel(template.funnelSettings), labelOf: labelOf })) : null, _jsx(LeftoverIssues, { renderedExtra: ladderDescs.map((d) => d.key), issues: serverIssues, launchDescs: launchDescs, tplValues: tplValues, labelOf: labelOf }), resumeConflict ? (_jsxs(InlineNotice, { variant: "danger", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "resume-conflict", children: [_jsxs("span", { children: ["\u041F\u0430\u0447\u043A\u0430 \u0441 \u044D\u0442\u0438\u043C \u043A\u043B\u044E\u0447\u043E\u043C \u0443\u0436\u0435 \u0441\u043E\u0437\u0434\u0430\u043D\u0430, \u043D\u043E \u043D\u0435 \u0432\u0441\u044F", resumeConflict.missing > 0
                                        ? ` (не создано: ${formatCount(resumeConflict.missing, ['запуск', 'запуска', 'запусков'])})`
                                        : '', ", \u0438 \u0434\u043E\u0441\u0442\u0440\u043E\u0438\u0442\u044C \u0435\u0451 \u043F\u043E \u043F\u043B\u0430\u043D\u0443 \u043D\u0435\u043B\u044C\u0437\u044F: \u0441 \u0442\u0435\u0445 \u043F\u043E\u0440 \u0438\u0437\u043C\u0435\u043D\u0438\u043B\u0438\u0441\u044C \u043F\u0440\u043E\u0444\u0438\u043B\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430, \u043C\u0435\u0434\u0438\u0430, \u0441\u043F\u0438\u0441\u043E\u043A \u0441\u0442\u0440\u0430\u043D \u0438\u043B\u0438 \u0440\u0435\u0435\u0441\u0442\u0440. \u041E\u0442\u043A\u0440\u043E\u0439\u0442\u0435 \u0435\u0451 \u0438 \u043E\u0442\u043C\u0435\u043D\u0438\u0442\u0435 \u043D\u0435\u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u043D\u044B\u0435 \u0437\u0430\u043F\u0443\u0441\u043A\u0438. \u0414\u043B\u044F \u043D\u043E\u0432\u043E\u0439 \u043F\u0430\u0447\u043A\u0438 \u0432\u0437\u044F\u0442 \u043D\u043E\u0432\u044B\u0439 \u043A\u043B\u044E\u0447 \u2014 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C\u00BB \u0435\u0449\u0451 \u0440\u0430\u0437."] }), resumeConflict.batchId ? (_jsx("div", { className: "mt-2", children: _jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/fb-launches/batches/$id", params: { id: resumeConflict.batchId }, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043F\u0430\u0447\u043A\u0443" }) }) })) : null] })) : null, keyTaken ? (_jsx(KeyTakenNotice, { keyTaken: keyTaken, canLaunch: perms.canLaunch, pending: createAnother.isPending, previewed: previewed, buildInput: (v) => buildInput(v, previewed?.input.activation ?? defaultActivation), blockedReason: blockedReason, onReset: () => setKeyTaken(null), onCreateAnother: () => createAnother.mutate() })) : null, duplicate ? (_jsx(InlineNotice, { variant: "info", bordered: true, icon: Info, as: "div", "data-testid": "dup-activation", children: DUPLICATE_ACTIVATION_TEXT })) : null, repeatInput ? (_jsxs("div", { className: "flex items-start gap-2 text-sm", "data-testid": "repeat-draft", children: [_jsx(Checkbox, { id: "repeat-draft", checked: draft, onCheckedChange: (v) => setDraft(v === true) }), _jsx(Label, { htmlFor: "repeat-draft", className: "[overflow-wrap:anywhere]", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A \u2014 \u0432 Meta \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u0441\u043E\u0437\u0434\u0430\u0451\u0442\u0441\u044F \u0438 \u043D\u0435 \u0442\u0440\u0430\u0442\u0438\u0442\u0441\u044F, \u043F\u043E\u043A\u0430 \u0432\u044B \u043D\u0435 \u043D\u0430\u0436\u043C\u0451\u0442\u0435 \u00AB\u0412\u044B\u043F\u043E\u043B\u043D\u0438\u0442\u044C\u00BB \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435 \u043F\u0430\u0447\u043A\u0438" })] })) : null, _jsx(StickyFooter, { draft: draft && repeatInput != null, canLaunch: perms.canLaunch, accounts: accounts, previewed: previewed, buildInput: (v) => buildInput(v, previewed?.input.activation ?? defaultActivation), busy: preview.isPending || create.isPending || createAnother.isPending, blockedReason: blockedReason, singleMode: duplicate ? 'manual' : scheduledActive ? defaultActivation : null, createHint: duplicate ? DUPLICATE_ACTIVATION_TEXT : undefined, byKey: byKey, funnelBlocked: funnelBlocked, onCreate: (mode) => void requestCreate(mode) })] }), v4 && previewed?.preview.spend != null ? (_jsx(SpendLaunchConfirmDialog, { open: confirm != null, onOpenChange: (o) => (!o ? setConfirm(null) : null), preview: previewed.preview, flow: createFlowOf(confirm?.mode ?? 'auto', tplValues['opt.createMode']), changed: confirm?.changed ?? false, draft: draft && repeatInput != null, note: confirmNote, onConfirm: () => confirm
                    ? create
                        .mutateAsync(confirm.mode)
                        .catch(() => undefined)
                        .finally(() => setConfirm(null))
                    : undefined })) : null, _jsx(ConfirmLaunchDialog, { open: confirm != null && !(v4 && previewed?.preview.spend != null), onOpenChange: (o) => (!o ? setConfirm(null) : null), preview: previewed?.preview ?? null, flow: createFlowOf(confirm?.mode ?? 'auto', tplValues['opt.createMode']), changed: confirm?.changed ?? false, draft: draft && repeatInput != null, note: confirmNote, onConfirm: () => {
                    if (confirm)
                        create.mutate(confirm.mode);
                    setConfirm(null);
                } }), _jsx(ConfirmDialog, { open: conflictOpen, onOpenChange: setConflictOpen, title: "\u0428\u0430\u0431\u043B\u043E\u043D \u0438\u0437\u043C\u0435\u043D\u0451\u043D", description: repeatInput
                    ? 'Пока вы заполняли запуск, шаблон сохранили заново. Загрузить новую версию? Форма заполнится по пачке заново — сверенная с новой версией шаблона.'
                    : 'Пока вы заполняли запуск, шаблон сохранили заново. Загрузить новую версию? Кабинеты и креативы придётся выбрать ещё раз.', cancelLabel: "\u041E\u0441\u0442\u0430\u0442\u044C\u0441\u044F", actionLabel: "\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043D\u043E\u0432\u0443\u044E \u0432\u0435\u0440\u0441\u0438\u044E", actionStyle: "primary", onConfirm: () => {
                    void qc.invalidateQueries({ queryKey: TEMPLATES_KEY }).then(onTemplateConflict);
                } })] }));
}
/**
 * Живые контрактные значения экрана: шаблон + изменения запуска + лесенка
 * (очищенная граница лесенки — пусто, а не значение шаблона). Подписка на
 * форму — только в маленьких блоках, а не во всём теле (ревью SB-4 P2-7).
 */
function useLaunchLive(props) {
    const { form } = useZodFormContext();
    const values = form.watch();
    const currency = props.currencyOf(props.selected);
    const key = JSON.stringify([values.overrides ?? null, values.ladder ?? null]);
    const { tplValues, launchDescs, ladderDescs } = props;
    const live = useMemo(() => {
        const [overrides, ladder] = JSON.parse(key);
        return {
            ...tplValues,
            ...fromFormValues(launchDescs, overrides ?? undefined, { currency }),
            ...Object.fromEntries(ladderDescs.map((d) => [d.key, undefined])),
            ...fromFormValues(ladderDescs, ladder ?? undefined, { currency }),
        };
    }, [tplValues, launchDescs, ladderDescs, currency, key]);
    props.liveRef.current = live;
    return { form, values, live, currency };
}
/** Строки шага 4 про поля, которые группы шаблона задают сами (v4 §11.2). */
function LaunchGroupsFieldNote(props) {
    const { live } = useLaunchLive(props);
    return (_jsx(LaunchFieldGroupsNote, { desc: props.desc, groups: props.tplGroups, live: live, tplValue: props.tplValues[props.desc.key] }));
}
/** Предупреждение «пол при Advantage+» в шаге 4 (v4 §11.1). */
function LaunchGenderNotice(props) {
    const { live } = useLaunchLive(props);
    return (_jsx(GenderAdvantageNotice, { values: live, prefix: props.launchDescs.some((x) => x.key === ADVANTAGE_KEY) ? 'overrides' : null }));
}
/**
 * Копия: ключ уже занят пачкой с другими значениями. «Создать ещё одну» доступна только по свежей
 * проверке без несохранённых правок копии; любое изменение значений формы сбрасывает плашку.
 */
function KeyTakenNotice({ keyTaken, canLaunch, pending, previewed, buildInput, blockedReason, onReset, onCreateAnother, }) {
    const { form } = useZodFormContext();
    const values = form.watch();
    const fresh = previewed != null && comparable(buildInput(values)) === comparable(previewed.input);
    useEffect(() => {
        if (!fresh && !pending)
            onReset();
    }, [fresh, pending, onReset]);
    return (_jsx(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "dup-key-taken", children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("span", { children: "\u0418\u0437 \u044D\u0442\u043E\u0439 \u043A\u043E\u043F\u0438\u0438 \u0443\u0436\u0435 \u0441\u043E\u0437\u0434\u0430\u043D\u0430 \u043F\u0430\u0447\u043A\u0430 \u0441 \u0434\u0440\u0443\u0433\u0438\u043C\u0438 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F\u043C\u0438." }), _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [keyTaken.batchId ? (_jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/fb-launches/batches/$id", params: { id: keyTaken.batchId }, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0441\u043E\u0437\u0434\u0430\u043D\u043D\u0443\u044E \u043F\u0430\u0447\u043A\u0443" }) })) : null, _jsx(Button, { type: "button", size: "sm", disabled: !canLaunch || pending || !fresh || blockedReason !== undefined, onClick: onCreateAnother, children: pending ? 'Создаём…' : 'Создать ещё одну с текущими значениями' })] })] }) }));
}
/** Шаг «5. Структура» ниже раскладки и копий: лесенка и «Получится» (v4 §11.2). */
function StructureStep(props) {
    const { form, values, live, currency } = useLaunchLive(props);
    const ladder = ladderState(live);
    const ladderOn = props.ladderDescs.length > 0 && ladder.cap && ladder.step > 0;
    const inputKey = props.previewed ? comparable(props.buildInput(values)) : '';
    const fresh = props.previewed != null && inputKey === comparable(props.previewed.input)
        ? props.previewed.preview
        : null;
    const copies = typeof values.copies === 'number' ? values.copies : 1;
    return (_jsxs(_Fragment, { children: [props.ladderDescs.length > 0 ? (_jsx(LadderFields, { descs: props.ladderDescs, prefix: "ladder", live: live, issues: props.issues, copies: copies })) : null, props.v4 && props.tplGroups.length > 0 ? (_jsx(LaunchGroups, { groups: props.tplGroups, live: live, descs: props.registry.fields, bidDesc: props.byKey.get(GROUP_BID_KEY), bidEditable: props.bidEditable, currency: currency, feature: props.groupsFeature, issues: props.issues, preview: fresh, copies: copies, templateId: props.template.id, onRetryFeature: props.onRetryHealth })) : null, props.v4 ? (_jsx(StructureOutcome, { preview: fresh, ladder: ladderOn
                    ? {
                        step: ladder.step,
                        down: ladder.down,
                        bidMinor: typeof live['adset.bidAmountMinor'] === 'number'
                            ? live['adset.bidAmountMinor']
                            : null,
                    }
                    : null, currency: currency, maxAdSetsPerCampaign: props.registry.adSetGroupRules?.maxAdSetsPerCampaign ?? MAX_ADSETS_PER_CAMPAIGN, counts: {
                    layout: values.layout,
                    accounts: (values.accounts ?? []).length,
                    items: (values.items ?? []).length,
                    copies,
                }, onSwitchLayout: () => form.setValue('layout', 'adsets_in_campaign', { shouldDirty: true }), templateId: props.template.id, hideBids: props.tplGroups.length > 0 && props.groupsFeature === 'on' })) : null] }));
}
/** Отдаёт наружу чтение текущих кабинетов и креативов формы (перенос при смене шаблона). */
function FormValuesBridge({ target, }) {
    const { form } = useZodFormContext();
    useEffect(() => {
        target.current = () => {
            const v = form.getValues();
            return { accounts: v.accounts ?? [], items: v.items ?? [] };
        };
        return () => {
            target.current = null;
        };
    }, [form, target]);
    return null;
}
/**
 * Деньги до «Проверить» (v4 §3.11 п. 2): вход формы → `POST /batches/spend`
 * с задержкой 300 мс; ответ — шагу 5 и футеру через контекст. Считать нечего
 * (нет кабинета или креатива) — без запроса.
 */
function SpendBridge({ enabled, buildInput, isValid, children, }) {
    const { form } = useZodFormContext();
    const values = form.watch();
    let input = null;
    let invalid = false;
    if (enabled && (values.accounts ?? []).length > 0) {
        if (!isValid(values))
            invalid = true;
        else {
            const built = buildInput(values);
            input = built.items.length > 0 ? built : null;
        }
    }
    return (_jsx(LaunchSpendProvider, { enabled: enabled, input: input, invalid: invalid, children: children }));
}
function SectionToggle({ open, onToggle, children, }) {
    return (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", "aria-expanded": open, onClick: onToggle, children: [open ? (_jsx(ChevronDown, { "aria-hidden": true, className: "h-4 w-4" })) : (_jsx(ChevronRight, { "aria-hidden": true, className: "h-4 w-4" })), children] }));
}
function FormBody(props) {
    const { form } = useZodFormContext();
    const [showTemplate, setShowTemplate] = useState(false);
    const selected = form.watch('accounts') ?? [];
    const currency = props.currencyOf(selected);
    const { currencyRef, onCurrencyChange, launchDescs, ladderDescs } = props;
    // Валюта, в которой УЖЕ лежат деньги формы: меняется только после пересчёта
    // полей ниже.
    const [fieldsCurrency, setFieldsCurrency] = useState(currency);
    useEffect(() => {
        const prev = currencyRef.current;
        currencyRef.current = currency;
        onCurrencyChange(currency);
        if (prev === currency)
            return;
        if (currencyScale(prev) === currencyScale(currency)) {
            setFieldsCurrency(currency);
            return;
        }
        // Другое число знаков (USD ↔ JPY): деньги формы — в валюте кабинета, а
        // значение — в minor units. Пересчитать поле, сохранив сумму в minor units
        // (бюджет шаблона 2500 — это $25 или ¥2 500, а не «25» в любой валюте).
        // Очищенное поле остаётся пустым — виджет кита показывает хранимое
        // значение, а не умолчание формы.
        const money = [
            ...launchDescs.map((d) => ({ d, prefix: 'overrides' })),
            ...ladderDescs.map((d) => ({ d, prefix: 'ladder' })),
        ];
        for (const { d, prefix } of money) {
            if (d.type !== 'money')
                continue;
            const name = `${prefix}.${fieldName(d.key)}`;
            const minor = fromUiValue(d, form.getValues(name), prev);
            if (typeof minor === 'number') {
                form.setValue(name, toUiValue(d, minor, currency), { shouldDirty: true });
            }
        }
        setFieldsCurrency(currency);
    }, [currency, currencyRef, onCurrencyChange, launchDescs, ladderDescs, form]);
    const start = form.watch('start');
    const first = props.firstAccountOf(selected);
    const accountNames = new Map(props.accounts.map((a) => [a.platformAccountId, a.name]));
    // Замечания к старту — и пачки, и запусков плана (`monday-start` и т. п. —
    // их считает auto-ads по фактическому времени старта каждого запуска),
    // одинаковые по запускам — одной строкой; пропадают, как только старт поменяли.
    const startIssues = groupIssues([...props.issues, ...props.launchIssues].filter((i) => i.fieldKey === 'schedule.start')).map((g) => g.issue);
    const byField = issuesByField(useIssuesUntilEdited([...props.issues.filter((i) => i.fieldKey !== 'schedule.start'), ...startIssues], { 'schedule.start': start }));
    const itemIssues = [...props.issues, ...props.launchIssues].filter((i) => i.itemIndex != null);
    const chosen = props.accounts.filter((a) => selected.includes(a.platformAccountId));
    const lead = props.tplValues['opt.immediateLeadMinutes'];
    return (_jsx(RegistryDataProvider, { value: {
            registry: props.registry,
            locales: props.locales ?? null,
            countryLists: props.countryLists,
            platformAccountId: first?.platformAccountId ?? null,
            audienceSlotNames: slotNamesOf(props.accounts),
            accountStartHours: chosen.map((a) => ({
                name: a.name,
                hour: a.profile?.startTimeLocal ?? null,
            })),
            currency: fieldsCurrency,
            immediateLeadMinutes: typeof lead === 'number' ? lead : null,
        }, children: _jsx(BodyWithContext, { ...props, selected: selected, accountNames: accountNames, byField: byField, itemIssues: itemIssues, showTemplate: showTemplate, setShowTemplate: setShowTemplate }) }));
}
function BodyWithContext(props) {
    const cta = props.byKey.get('creative.cta');
    const startLabel = props.startDesc?.label ?? 'Старт';
    // шаг 4: не только деньги — подзаголовки групп реестра (v4 §11.2)
    const extended = props.v4 && props.launchDescs.some((d) => !MONEY_LAUNCH_KEYS.has(d.key));
    return (_jsxs(_Fragment, { children: [_jsx(SectionCard, { title: "2. \u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", children: _jsx(ZodField, { name: "accounts", render: ({ field, fieldState }) => (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(AccountChips, { accounts: props.accounts, selected: field.value ?? [], onChange: field.onChange, byKey: props.byKey, allowed: props.template.platformAccountIds }), fieldState.error ? (_jsx("span", { role: "alert", className: "text-xs text-[var(--color-state-danger-fg)]", children: fieldState.error.message })) : null] })) }) }), _jsx(SectionCard, { title: "3. \u041A\u0440\u0435\u0430\u0442\u0438\u0432\u044B", children: _jsx(ZodField, { name: "items", render: ({ field, fieldState }) => (_jsx(CreativeItems, { value: field.value ?? [], onChange: field.onChange, accountIds: props.selected, accountNames: props.accountNames, accounts: props.accounts
                            .filter((a) => a.platformEnabled)
                            .map((a) => ({ id: a.platformAccountId, name: a.name })), accountPages: new Map(props.accounts.map((a) => [a.platformAccountId, a.profile?.pageId ?? null])), canUpload: props.canUpload, health: props.health, ctaDescriptor: cta, defaultCta: props.tplValues['creative.cta'] ?? null, defaultLink: props.tplValues['creative.link'] ?? null, issues: props.itemIssues, error: fieldState.error?.message })) }) }), _jsx(SectionCard, { title: extended ? '4. Бюджет, ставка и изменения шаблона' : '4. Бюджет и ставка', children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(RegistryFields, { descriptors: props.launchDescs, prefix: "overrides", issues: props.issues, contextValues: props.tplValues, ...(extended ? { grouped: 'headings' } : {}), renderExtra: (d) => (_jsxs(_Fragment, { children: [d.key === GENDERS_KEY ? _jsx(LaunchGenderNotice, { ...props }) : null, props.v4 && props.groupsFeature === 'on' ? (_jsx(LaunchGroupsFieldNote, { ...props, desc: d })) : null] })) }), _jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs(SectionToggle, { open: props.showTemplate, onToggle: () => props.setShowTemplate(!props.showTemplate), children: ["\u0418\u0437 \u0448\u0430\u0431\u043B\u043E\u043D\u0430 \u00AB", props.template.name, "\u00BB (\u0442\u043E\u043B\u044C\u043A\u043E \u0447\u0442\u0435\u043D\u0438\u0435)"] }), props.showTemplate ? (_jsxs("div", { className: "flex flex-col gap-3 pl-2", children: [_jsx(ValueChips, { descriptors: props.readOnly, values: props.tplValues }), _jsx("span", { className: "text-xs font-medium text-[var(--color-text-secondary)]", children: "\u0417\u0430\u0444\u0438\u043A\u0441\u0438\u0440\u043E\u0432\u0430\u043D\u043E \u0441\u0438\u0441\u0442\u0435\u043C\u043E\u0439" }), _jsx(FixedFieldsList, { descriptors: props.fixed })] })) : null] })] }) }), _jsx(SectionCard, { title: props.v4 ? '5. Структура' : '5. Копии', children: _jsxs("div", { className: "flex flex-col gap-4", children: [props.v4 ? _jsx(ZodField, { name: "layout" }) : null, _jsx("div", { className: "max-w-xs", children: _jsx(ZodField, { name: "copies" }) }), props.v4 ? null : _jsx(ZodField, { name: "layout" }), !props.v4 && props.tplGroups.length > 0 ? (_jsx(InlineNotice, { variant: "danger", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "launch-groups-off", children: GROUPS_OFF_LAUNCH_TEXT })) : null, _jsx(StructureStep, { ...props })] }) }), _jsx(SectionCard, { title: "6. \u0421\u0442\u0430\u0440\u0442", hint: _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0432\u0440\u0435\u043C\u044F \u041B\u043E\u0441-\u0410\u043D\u0434\u0436\u0435\u043B\u0435\u0441\u0430" }), children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(ZodField, { name: "start", as: StartWidget, label: startLabel }), _jsx(FieldIssues, { issues: props.byField.get('schedule.start') ?? [] })] }) }), hasTemplateFunnel(props.template.funnelSettings) ? (_jsx(LaunchFunnelStep, { model: props.funnel, title: "7. \u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438", control: props.funnelControl })) : null] }));
}
/**
 * Проблемы от auto-ads (400 `invalid-values` и т. п.), которым на экране нет
 * места: без поля (`fieldKey: null`), по полю, которое здесь не нарисовано
 * (спрятано `visibleWhen`, не редактируется при запуске, опции пачки
 * `opt.*`), по креативу, которого уже нет. Иначе они терялись — тост
 * говорил «ошибка», а где именно, не было видно нигде.
 */
function LeftoverIssues({ issues, launchDescs, tplValues, labelOf, renderedExtra = [], }) {
    const { form } = useZodFormContext();
    const watched = form.watch('overrides');
    const items = form.watch('items') ?? [];
    if (issues.length === 0)
        return null;
    // Тот же контекст видимости, что у `RegistryFields` блока «Бюджет и ставка».
    const current = { ...tplValues, ...fromFormValues(launchDescs, watched) };
    const rendered = new Set(launchDescs
        .filter((d) => !NON_FORM_TYPES.has(d.type) && isVisible(d, current))
        .map((d) => d.key));
    rendered.add('schedule.start');
    for (const k of renderedExtra)
        rendered.add(k);
    const leftover = issues.filter((i) => i.itemIndex != null
        ? i.itemIndex >= items.length
        : i.fieldKey == null || !rendered.has(i.fieldKey));
    if (leftover.length === 0)
        return null;
    return (_jsx("div", { "data-testid": "leftover-issues", children: _jsx(IssuesList, { issues: leftover, labelOf: labelOf }) }));
}
function StalePreview({ previewed, buildInput, registry, byKey, accounts, tplValues, funnel, showFunnel, labelOf, }) {
    const { form } = useZodFormContext();
    const countryLists = useCountryLists();
    const values = form.watch();
    const current = buildInput(values);
    const stale = comparable(current) !== comparable(previewed.input);
    const effective = { ...tplValues, ...previewed.input.overrides };
    const labels = planLabels(values.items ?? [], byKey, {
        countryLists: countryLists.data?.items ?? [],
        accountNames: new Map(accounts.map((a) => [a.platformAccountId, a.name])),
        roasFloor: effective['adset.roasFloor'],
    });
    return (_jsxs(_Fragment, { children: [_jsx(PreviewPanel, { preview: previewed.preview, labelOf: labelOf, guaranteeDescriptors: registry.guarantees, stale: stale, labels: labels }), showFunnel || previewed.input.funnelSettings || previewed.preview.funnelSettings ? (_jsx(FunnelPlanSummary
            // итог считает auto-ads (новый); старый не присылает — шаблон + изменения пачки
            , { 
                // итог считает auto-ads (новый); старый не присылает — шаблон + изменения пачки
                values: previewed.preview.funnelSettings?.values ??
                    effectiveFunnelValues(funnel.values, previewed.input.funnelSettings), catalog: funnel.catalog })) : null] }));
}
/**
 * Подписи плана, которые экран знает лучше сервера (v4 D8, D9, D15, D16):
 * креатив строки — по креативам экрана (пост — «его текст», ««hook.mp4» · Уже в
 * рекламе · GOOSE»), таргетинг групп — названием списка стран и подписями
 * вариантов вместо кодов Meta, порог ROAS — из шаблона и изменений запуска.
 */
export function planLabels(items, byKey, ctx) {
    const byAsset = new Map();
    const byPost = new Map();
    for (const i of items) {
        if (i.kind === 'post') {
            const t = i.post?.text?.replace(/\s+/g, ' ').trim();
            if (t)
                byPost.set(i.objectStoryId, `«${t.length > 60 ? `${t.slice(0, 60)}…` : t}»`);
            continue;
        }
        const src = MEDIA_SOURCE_LABEL_RU[i.file.source] ?? i.file.source;
        const from = i.file.origin?.platformAccountId;
        const account = from != null ? (ctx.accountNames.get(from) ?? null) : null;
        byAsset.set(i.assetId, [`«${i.file.name}»`, src, account].filter(Boolean).join(' · '));
    }
    return {
        displayOf: (t) => humanizeDisplay(t.display, byKey.get(t.fieldKey), ctx),
        creativeLabelOf: (c) => c.kind === 'post'
            ? c.objectStoryId != null
                ? (byPost.get(c.objectStoryId) ?? null)
                : null
            : c.assetId != null
                ? (byAsset.get(c.assetId) ?? null)
                : null,
        roasFallback: typeof ctx.roasFloor === 'number' ? ctx.roasFloor : null,
    };
}
/** Пояснение под единственной кнопкой «как в Ads Manager» — по режиму включения шаблона. */
export const SINGLE_CREATE_HINT = {
    auto: 'Кампании создаются включёнными и стартуют в указанное время (время Лос-Анджелеса); до старта показов нет, ошибка сверки — пауза.',
    manual: 'В шаблоне включение вручную: всё создаётся на паузе, включите запуски на карточке пачки.',
};
/**
 * Липкий футер: «Проверить» → план; создание — только по свежему плану без
 * ошибок. «Как в Ads Manager» (`singleMode`) — одна кнопка по режиму включения
 * шаблона: «Создать» (включёнными, стартуют в указанное время) или «Создать на
 * паузе»; шаблон «на паузе» — «Создать на паузе» / «Создать и включить».
 */
function StickyFooter({ draft = false, canLaunch, accounts, previewed, buildInput, busy, singleMode, onCreate, byKey, funnelBlocked, createHint, blockedReason, }) {
    const { form } = useZodFormContext();
    const values = form.watch();
    const ready = allReady(accounts, values.accounts ?? []);
    const current = buildInput(values);
    const fresh = previewed != null && comparable(current) === comparable(previewed.input);
    const canCreate = canLaunch &&
        fresh &&
        previewed?.preview.canCreate === true &&
        !busy &&
        blockedReason === undefined;
    // итог дня — всегда, когда он отличается от поля «Дневной бюджет» (v4 §11.2, до центов)
    const spendState = useLaunchSpend();
    const spendText = spendFooterText(spendState.data);
    return (_jsxs("div", { className: "sticky bottom-0 z-10 mt-4 -mx-[var(--page-pad-x,1rem)] flex flex-wrap items-center gap-2 border-t border-[var(--color-border-subtle)] bg-[var(--color-surface-canvas)] px-[var(--page-pad-x,1rem)] pt-3 pb-3 shadow-[0_-4px_8px_-6px_rgb(0_0_0/0.15)] sm:-mx-[var(--page-pad-x,1.5rem)] sm:px-[var(--page-pad-x,1.5rem)]", "data-testid": "launch-footer", children: [spendText ? (_jsxs("span", { className: `basis-full text-base font-semibold tabular-nums sm:basis-auto${spendState.pending ? ' opacity-60' : ''}`, "aria-live": "polite", "aria-busy": spendState.pending || undefined, "data-testid": "footer-spend", children: [spendText, spendState.pending ? (_jsx("span", { className: "ml-2 text-xs font-normal text-[var(--color-text-secondary)]", children: "\u043F\u0435\u0440\u0435\u0441\u0447\u0438\u0442\u044B\u0432\u0430\u0435\u043C\u2026" })) : null] })) : null, !canLaunch ? (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u0421\u043E\u0437\u0434\u0430\u0432\u0430\u0442\u044C \u0437\u0430\u043F\u0443\u0441\u043A\u0438 \u043C\u043E\u0433\u0443\u0442 \u0441\u0443\u043F\u0435\u0440\u0432\u0430\u0439\u0437\u0435\u0440 \u0438 \u0432\u043B\u0430\u0434\u0435\u043B\u0435\u0446." })) : blockedReason !== undefined ? (_jsx("span", { className: "text-sm text-[var(--color-state-warning-fg)]", "data-testid": "footer-blocked-reason", children: blockedReason })) : funnelBlocked ? (_jsx("span", { className: "text-sm text-[var(--color-state-warning-fg)]", "data-testid": "funnel-blocked-reason", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u043A\u0430 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430 \u2014 \u0441\u043F\u0438\u0441\u043E\u043A \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u0441\u0435\u0439\u0447\u0430\u0441 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D (\u0448\u0430\u0433 7)." })) : !ready && (values.accounts ?? []).length > 0 ? (_jsxs("span", { className: "text-sm text-[var(--color-state-warning-fg)]", "data-testid": "not-ready-reason", children: ["\u041F\u0440\u043E\u0432\u0435\u0440\u043A\u0430 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430 \u2014", ' ', firstNotReadyText(accounts, values.accounts ?? [], byKey) ?? 'кабинет не готов', "."] })) : null, _jsxs("div", { className: "ml-auto flex flex-wrap gap-2", children: [_jsx(SubmitButton, { variant: "secondary", pendingLabel: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C\u2026", disabled: !canLaunch || !ready || busy || funnelBlocked || blockedReason !== undefined, children: "\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C" }), draft ? (_jsx(Button, { type: "button", disabled: !canCreate, onClick: () => onCreate(previewed?.input.activation ?? singleMode ?? 'manual'), children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A" })) : singleMode ? (_jsx(Button, { type: "button", disabled: !canCreate, onClick: () => onCreate(singleMode), children: CREATE_FLOW_ACTION[singleMode === 'manual' ? 'manual' : 'scheduled'] })) : (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", variant: "secondary", disabled: !canCreate, onClick: () => onCreate('manual'), children: CREATE_FLOW_ACTION.manual }), _jsx(Button, { type: "button", disabled: !canCreate, onClick: () => onCreate('auto'), children: CREATE_FLOW_ACTION.auto })] }))] }), singleMode && !draft ? (_jsx("span", { className: "basis-full text-right text-xs text-[var(--color-text-muted)]", "data-testid": "create-hint", children: createHint ?? SINGLE_CREATE_HINT[singleMode] })) : null] }));
}
//# sourceMappingURL=new-launch.js.map